import { useSharedProperties } from '@unboared/react';

import { FP_STATE_KEY, FreePlayState } from '~/services/freePlay/types';
import { VoteService } from '~/services/freePlay/VoteService';

export type FreePlayView = FreePlayState & {
    /** True once the host has broadcast a state, i.e. the orchestrator is running. */
    enabled: boolean;
};

/**
 * Reactive read of the free-play state broadcast by the host.
 * Same return value on every device (host + gamepads).
 *
 * Wrapped in try/catch because `getSharedProperty` resolves the host device via
 * `API.getHostID()` → `getDevice(0)` which throws when the host isn't registered
 * yet on the gamepad side (just-after-join race, or TV not connected). Treat as
 * disabled until the device list syncs; the `useSharedPropertyChange` listener
 * triggers a re-render once the host appears. Same idea as gotcha #16.
 */
export function useFreePlayState(): FreePlayView {
    const { getSharedProperty } = useSharedProperties();
    let stored: FreePlayState | undefined;
    try {
        stored = getSharedProperty(FP_STATE_KEY) as FreePlayState | undefined;
    } catch {
        stored = undefined;
    }
    return {
        enabled: stored !== undefined,
        ...(stored ?? VoteService.initial()),
    };
}
